import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  HostBinding,
  HostListener,
  inject,
  input,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { AuthService } from '../../../features/auth/services/auth.service';
import { useBreakpoint } from '../../composables/useBreakpoint';
import { HeaderV2NavigationComponent } from './components/navigation/header-v2-navigation.component';
import { HeaderV2AvatarComponent } from './components/avatar/header-v2-avatar.component';
import { auth as AuthGen } from '@valvur/shared-grpc';

export interface NavigationButton {
  icon: string;
  alt: string;
  route?: string | null;
  action?: () => void;
  isActive?: boolean;
  /** Exact path match for routerLinkActive (e.g. `identity` vs `identity/integration-requests`). */
  exactRoute?: boolean;
}

@Component({
  selector: 'app-header-v2',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    HeaderV2NavigationComponent,
    HeaderV2AvatarComponent,
  ],
  templateUrl: './header-v2.component.html',
  styleUrl: './header-v2.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class HeaderV2Component {
  #authService = inject(AuthService);
  #router = inject(Router);

  navigationButtons = input<ReadonlyArray<NavigationButton>>([]);
  layout = input<'admin' | 'school' | 'patrol'>('admin');
  /** Optional user override (e.g. admin). When provided, used instead of teacher. */
  user = input<AuthGen.User | undefined>(undefined);

  readonly showUserMenu = signal(false);

  readonly displayUser = computed(() => this.user());

  readonly displayName = computed(() => {
    const user = this.displayUser();
    if (!user) {
      return '';
    }
    const first = user.personalData?.firstName?.trim();
    const last = user.personalData?.lastName?.trim();
    const fullName = [first, last].filter(Boolean).join(' ');
    return fullName || user.userName || '';
  });

  readonly displayEmail = computed(() => {
    const user = this.displayUser();
    return user?.personalData?.email?.trim() || user?.userName?.trim() || '';
  });

  readonly showProfileAction = computed(() => {
    const layout = this.layout();
    return layout === 'admin' || layout === 'patrol';
  });

  logout(): void {
    this.showUserMenu.set(false);
    this.#authService.logout();
  }

  goToProfile(): void {
    if (!this.showProfileAction()) {
      return;
    }
    this.showUserMenu.set(false);
    void this.#router.navigate(['profile']);
  }

  toggleUserMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.showUserMenu.update((open) => !open);
  }

  @HostListener('document:click')
  closeUserMenu(): void {
    this.showUserMenu.set(false);
  }

  readonly currentBreakpoint = useBreakpoint().currentBreakpoint;
  readonly isMobile = computed(
    () =>
      this.currentBreakpoint() === 'XSmall' ||
      this.currentBreakpoint() === 'Small',
  );

  readonly showUserActions = computed(() => {
    if (!this.isMobile() || this.layout() !== 'admin') {
      return true;
    }
    return !!this.displayUser()?.personalData?.avatar;
  });

  readonly centerNavigation = computed(
    () => this.isMobile() && this.layout() === 'admin',
  );

  @HostBinding('class.is-mobile')
  get hostIsMobile(): boolean {
    return this.isMobile();
  }

  @HostBinding('class.header-v2--admin')
  get hostIsAdmin(): boolean {
    return this.layout() === 'admin';
  }
}
